import React from 'react';

import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import { noop } from 'lodash';
import { useIntl } from 'react-intl';

import { ICartEntry } from '@rbi-ctg/menu';
import { useRedeemReward } from 'components/cart-item/redeem-reward/use-redeem-reward/use-redeem-reward';
import Incrementor, { IncrementorActions } from 'components/incrementor';
import { useGetUrlSearchParam } from 'hooks/location/use-get-url-search-param';
import { useCartItemEditingTools } from 'hooks/use-cart-item-editing-tools';
import { useIsEditCartEnabled } from 'hooks/use-is-edit-cart-enabled';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { IParsedLoyaltyReward } from 'state/loyalty/hooks/types';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyRewards } from 'state/loyalty/hooks/use-loyalty-rewards';
import { useOrderContext } from 'state/order';
import { useProductWizardContext } from 'state/product-wizard';
import { MIN_CART_QUANTITY, getMenuObjectCartQuantity } from 'utils/cart';

import { ActionsContainer } from '../styled/actions-container';
import { CartEntryTitle } from '../styled/cart-entry-title';
import { CartItemButton } from '../styled/cart-item-button';
import {
  ICartPreviewActions,
  ICartPreviewIncrementor,
  ICartPreviewTitle,
  ICartRemoveButton,
} from '../types';

type State = {
  isEditCartEnabled: boolean;
  handleEditItem: VoidFunction;
  handleRemoveItem: VoidFunction;
  handleQuantityChange: (value: number, incrementorAction: IncrementorActions) => void;
  item?: ICartEntry;
};

const CartPreviewContext = React.createContext<State>({
  isEditCartEnabled: false,
  handleEditItem: noop,
  handleRemoveItem: noop,
  handleQuantityChange: noop,
});

const CartPreviewActions: React.FC<ICartPreviewActions> = ({ children, isReward, item }) => {
  const isEditCartEnabled = useIsEditCartEnabled({ cartHasIncentive: isReward });
  const { handleEditItem, handleRemoveItem, handleQuantityChange } = useCartItemEditingTools({
    item,
  });
  return (
    <CartPreviewContext.Provider
      value={{
        item,
        isEditCartEnabled,
        handleEditItem,
        handleRemoveItem,
        handleQuantityChange,
      }}
    >
      <ActionsContainer>{children}</ActionsContainer>
    </CartPreviewContext.Provider>
  );
};

export const CartPreviewTitle: React.FC<ICartPreviewTitle> = ({ item }) => (
  <CartEntryTitle data-testid="cart-preview-name">{item.name}</CartEntryTitle>
);

export const CartPreviewEditButton: React.FC = () => {
  const { formatMessage } = useIntl();
  const { item, isEditCartEnabled, handleEditItem } = React.useContext(CartPreviewContext);

  const onClickCallback = () => {
    handleEditItem();
  };

  return isEditCartEnabled ? (
    <CartItemButton
      onClick={onClickCallback}
      aria-label={`${formatMessage({ id: 'edit' })} ${item?.name ?? ''} `}
      data-testid="cart-preview-edit-button"
    >
      {formatMessage({
        id: 'edit',
      })}
    </CartItemButton>
  ) : null;
};

export const CartPreviewRemoveButton: React.FC<ICartRemoveButton> = ({ isRewardCartDiscount }) => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { item, handleRemoveItem } = React.useContext(CartPreviewContext);

  const onClickCallback = () => {
    if (isRewardCartDiscount) {
      dispatch(
        actions.loyalty.removeAppliedReward({
          rewardBenefitId: (item as unknown as IParsedLoyaltyReward).rewardId,
          cartId: 'discount-offer',
        })
      );
    } else {
      handleRemoveItem();
    }
  };

  return (
    <CartItemButton onClick={onClickCallback} aria-label={`Remove ${item?.name ?? ''}`}>
      {formatMessage({
        id: 'remove',
      })}
    </CartItemButton>
  );
};

export const CartPreviewIncrementor: React.FC<ICartPreviewIncrementor> = ({ children, border }) => {
  const { item, handleQuantityChange } = React.useContext(CartPreviewContext);
  const { incentiveNotInMenu, limitPerOrderMet } = useRedeemReward(item);
  const { cartEntries } = useOrderContext() ?? {};
  const { availableCartQuantity, maxCartQuantityMet } = getMenuObjectCartQuantity({
    menuObject: item,
    cartEntries,
  });
  const loyaltyUser = useAppSelector(selectors.loyalty.selectUser);
  const { rewardLimitePerOrder } = useLoyaltyRewards(loyaltyUser);
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const itemQuantity = item?.quantity ?? 0;
  const rewardRedemptionBenefitIdParam = useGetUrlSearchParam('reward');
  const maxRewardToRedeem = rewardLimitePerOrder || availableCartQuantity;
  const { productQuantity } = useProductWizardContext();
  const { rewardPointCost } = useRedeemReward();
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);
  const isIncrementDisabledForRewardRedemption = () => {
    if (limitPerOrderMet) {
      return true;
    }
    if (!incentiveNotInMenu || !loyaltyEnabled) {
      return false;
    }

    if (rewardRedemptionBenefitIdParam) {
      return productQuantity === rewardLimitePerOrder;
    }

    const checkRedemptionEligibility = () => {
      if (rewardRedemptionBenefitIdParam) {
        const incrementedPointCost = rewardPointCost * (productQuantity + 1);
        return stagedCartPoints <= incrementedPointCost;
      }
      return false;
    };
    return checkRedemptionEligibility();
  };
  const disableIncrementQuantity = maxCartQuantityMet || isIncrementDisabledForRewardRedemption();
  const disableIncrementAndDecrement = loyaltyEnabled && incentiveNotInMenu;

  return (
    <Incrementor
      name={item?.name ?? ''}
      aria-label={`Quantity ${itemQuantity} of ${item?.name ?? ''}`}
      data-testId="cart-preview-quantity"
      value={itemQuantity}
      onChange={handleQuantityChange}
      min={MIN_CART_QUANTITY}
      border={border}
      max={
        !!rewardRedemptionBenefitIdParam ? maxRewardToRedeem : availableCartQuantity + itemQuantity
      }
      disableIncrement={disableIncrementQuantity}
      disabled={disableIncrementAndDecrement}
    >
      {children}
    </Incrementor>
  );
};

export const CartPreviewVisuallyHidden: React.FC = () => {
  const { formatMessage } = useIntl();
  const { item } = React.useContext(CartPreviewContext);

  return (
    <VisuallyHidden role="alert">
      {formatMessage(
        { id: 'quantityUpdatedAlert' },
        {
          quantity: item?.quantity ?? 0,
          itemName: item?.name ?? '',
        }
      )}
    </VisuallyHidden>
  );
};

export default CartPreviewActions;
